import React, { useState, useContext } from "react";
import {
  signIn,
  confirmSignIn,
  resetPassword,
  confirmResetPassword,
} from "@aws-amplify/auth";
import { useNavigate } from "react-router-dom";
import { Center, Stack, Input, Button, PasswordInput } from "@mantine/core";
import {
  UserContext,
  UserContextType,
} from "../contexts/UserContext/UserContext";
import { Btn } from "../components/Generics/Btn";
import { Text } from "../components/Generics/Text";

/**
 * Combined sign‑in & forgot‑password flow.
 *
 * STAGES
 * ───────────────────────────────────────────────
 *  SIGN_IN            – Default username + password form
 *  NEW_PASSWORD       – Force‑password‑change challenge
 *  FORGOT_REQUEST     – Ask username/email, send reset code
 *  FORGOT_SUBMIT      – Enter code + new password to complete reset
 */
const SignIn: React.FC = () => {
  type Stage = "SIGN_IN" | "NEW_PASSWORD" | "FORGOT_REQUEST" | "FORGOT_SUBMIT";

  const [stage, setStage] = useState<Stage>("SIGN_IN");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [message, setMessage] = useState(" ");
  const userContext = useContext(UserContext) as UserContextType;
  const navigate = useNavigate();

  // ─────────────────────────────────────── SIGN IN
  const handleSignIn = async () => {
    setMessage("Signing in…");
    try {
      const { isSignedIn, nextStep } = await signIn({ username, password });

      if (isSignedIn) {
        userContext.setUser({ username });
        navigate("/");
      } else {
        switch (nextStep.signInStep) {
          case "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED":
            setStage("NEW_PASSWORD");
            setMessage("Please choose a new password.");
            break;
          default:
            setMessage(`Unexpected sign‑in step: ${nextStep.signInStep}`);
        }
      }
    } catch (error: any) {
      console.error("Error during sign‑in", error);
      setMessage(
        error.code === "NotAuthorizedException"
          ? "Incorrect username or password."
          : "Failed to sign in.",
      );
    }
  };

  // ───────────────────────────────── NEW PASSWORD CHALLENGE
  const handleCompleteNewPassword = async () => {
    try {
      const { isSignedIn } = await confirmSignIn({
        challengeResponse: newPassword,
      });

      if (isSignedIn) {
        userContext.setUser({ username });
        navigate("/");
      }
    } catch (error) {
      console.error("Error completing new‑password challenge", error);
      setMessage("Failed to set new password.");
    }
  };

  // ────────────────────────────────── RESET PASSWORD (step 1)
  const handleForgotPasswordRequest = async () => {
    try {
      const { nextStep } = await resetPassword({ username });

      if (nextStep.resetPasswordStep === "CONFIRM_RESET_PASSWORD_WITH_CODE") {
        setStage("FORGOT_SUBMIT");
        setMessage("A verification code has been sent to your email.");
      } else {
        setMessage("Unexpected next step.");
      }
    } catch (err: any) {
      console.error("Error sending reset code", err);
      setMessage(err?.message ?? "Failed to send reset code.");
    }
  };

  // ────────────────────────────────── RESET PASSWORD (step 2)
  const handleForgotPasswordSubmit = async () => {
    try {
      await confirmResetPassword({
        username,
        confirmationCode: verificationCode,
        newPassword,
      });
      setMessage("Password reset successfully — you can now sign in.");
      setStage("SIGN_IN");
      setPassword("");
      setNewPassword("");
      setVerificationCode("");
    } catch (err: any) {
      console.error("Error confirming reset password", err);
      setMessage(err?.message ?? "Failed to reset password.");
    }
  };

  const renderSignIn = () => (
    <>
      <Input
        placeholder="@Email"
        value={username}
        onChange={(e) => setUsername(e.target.value)}
      />
      <PasswordInput
        placeholder="Password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
      />
      <Btn action={handleSignIn}>
        <Text weight="bold" color="#ffffff">
          Sign In
        </Text>
      </Btn>
      <Button
        variant="subtle"
        size="xs"
        maw={120}
        mx="auto"
        onClick={() => setStage("FORGOT_REQUEST")}
      >
        Forgot password?
      </Button>
    </>
  );

  const renderNewPassword = () => (
    <>
      <PasswordInput
        placeholder="New Password"
        value={newPassword}
        onChange={(e) => setNewPassword(e.target.value)}
      />
      <Btn action={handleCompleteNewPassword}>
        <Text weight="bold" color="#ffffff">
          Set New Password
        </Text>
      </Btn>
    </>
  );

  const renderForgotRequest = () => (
    <>
      <Input
        placeholder="@Email"
        value={username}
        onChange={(e) => setUsername(e.target.value)}
      />
      <Btn action={handleForgotPasswordRequest}>
        <Text weight="bold" color="#ffffff">
          Send Reset Code
        </Text>
      </Btn>
      <Button
        variant="subtle"
        size="xs"
        maw={120}
        mx="auto"
        onClick={() => setStage("SIGN_IN")}
      >
        Back to sign in
      </Button>
    </>
  );

  const renderForgotSubmit = () => (
    <>
      <Input
        placeholder="Verification code"
        value={verificationCode}
        onChange={(e) => setVerificationCode(e.target.value)}
      />
      <PasswordInput
        placeholder="New password"
        value={newPassword}
        onChange={(e) => setNewPassword(e.target.value)}
      />
      <Btn action={handleForgotPasswordSubmit}>
        <Text weight="bold" color="#ffffff">
          Reset Password
        </Text>
      </Btn>
    </>
  );

  const renderStage = () => {
    switch (stage) {
      case "SIGN_IN":
        return renderSignIn();
      case "NEW_PASSWORD":
        return renderNewPassword();
      case "FORGOT_REQUEST":
        return renderForgotRequest();
      case "FORGOT_SUBMIT":
        return renderForgotSubmit();
      default:
        return null;
    }
  };

  return (
    <Center mt="20vh">
      <Stack maw={360} w="100%" px="sm">
        <Text size="large">
          {stage === "SIGN_IN" && "Sign In"}
          {stage === "NEW_PASSWORD" && "Choose a New Password"}
          {stage === "FORGOT_REQUEST" && "Reset Your Password"}
          {stage === "FORGOT_SUBMIT" && "Enter Verification Code"}
        </Text>
        {renderStage()}
        {!!message && <Text size="small">{message}</Text>}
      </Stack>
    </Center>
  );
};

export default SignIn;
